import { useState, useContext, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { t } from 'ttag'
import { useQuery } from 'react-query'

import { api } from '@h2/api'
import { Globals } from '@h2/globals'
import { Button } from '@h2/components/buttons/button'
import { SLB } from '@h2/components/colors'
import { Page } from '@h2/common/components/page'
import { Modal } from '@h2/components/containers/modal'
import { Flex } from '@h2/components/layout/flex'
import { Skeleton } from '@h2/components/loading/skeleton'

import { MuseoHeading } from './components/common/museo'
import { Resources } from './components/resources'
import { Lessons } from './components/lessons'
import { LegacyGame } from './components/legacy/legacyGame'

import { NavigatorConnection } from '../account/panels/navigatorConnection'

const StyledContent = styled(Page.Content)`
  border: ${SLB} solid 20px;
  border-radius: 55px;
  padding: 25px;
`
const StyledBottom = styled(Page.Bottom)`
  display: flex;
  justify-content: center;
`
const Avatar = styled.img`
  width: 95px;
  height: 95px;
`
const EarlyLearning = () => {
  const {
    userProfile,
    isNavianceElementary,
    setEarlyDashboard,
    handleOnViewChange,
    isEarlyLearnerMode,
    showNewElementaryDashboard
  } = useContext(Globals)
  // Avatar and Name
  const { data: gameData, isFetching: isFetchingGame } = useQuery(
    ['game-listing', userProfile],
    () => api.fetch('/early/utl')
  )
  const avatarURL = gameData?.games[0]?.avatarUrl
  const firstName = userProfile?.firstName
  // Most Recent Activity
  const { data: mostRecentActivity, isFetching: isFetchingMostRecentActivity } = useQuery(
    'user-assignments',
    () => {
      return api.fetch(`/plans/list`, {
        method: 'GET',
        version: 'v1'
      })
    }
  )
  const returningUser =
    mostRecentActivity?.filter(lesson => lesson.started === true).length > 0
  const [openModal, setOpenModal] = useState(false)
  const shouldShowExit =
    isEarlyLearnerMode &&
    (!isNavianceElementary || (isNavianceElementary && userProfile.hasManager))
  const handleToggle = useCallback(() => {
    if (isNavianceElementary) {
      return handleOnViewChange()
    }

    return setEarlyDashboard(false)
  }, [setEarlyDashboard, isNavianceElementary, handleOnViewChange])
  const welcomeString = useMemo(() => {
    const nameString = firstName ? firstName : userProfile.email

    return returningUser ? t`Welcome back, ${nameString}!` : t`Welcome, ${nameString}!`
  }, [firstName, returningUser, userProfile.email])

  return (
    <Page>
      <Page.Layout>
        <StyledContent>
          {isFetchingGame || isFetchingMostRecentActivity ? (
            <Flex>
              <Skeleton display="block" height="90px" margin="0px 0px 20px 0px" />
            </Flex>
          ) : (
            <Flex alignItems="center" margin={'0px 0px 20px 0px'}>
              {avatarURL && showNewElementaryDashboard && (
                <Avatar src={avatarURL} alt="avatar" />
              )}
              <MuseoHeading level="2" removeMargins={avatarURL && true}>
                {welcomeString}
              </MuseoHeading>
            </Flex>
          )}
          {showNewElementaryDashboard ? (
            <Lessons
              isFetchingMostRecentActivity={isFetchingMostRecentActivity}
              mostRecentActivity={mostRecentActivity}
            />
          ) : (
            <LegacyGame />
          )}
          <Resources setOpenModal={setOpenModal} />
          <Modal isOpen={openModal} onClose={() => setOpenModal(false)}>
            <Modal.Header />
            <Modal.Body>
              <NavigatorConnection />
            </Modal.Body>
          </Modal>
        </StyledContent>
        <StyledBottom>
          <Button variant="link">
            {shouldShowExit && (
              <MuseoHeading
                removeMargins={true}
                level="4"
                color={SLB}
                onClick={handleToggle}>
                {isNavianceElementary
                  ? t`Switch to Navigator Mode`
                  : t`Switch to Advanced View`}
              </MuseoHeading>
            )}
          </Button>
        </StyledBottom>
      </Page.Layout>
    </Page>
  )
}

export { EarlyLearning }
